import { PayloadAction, createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit';
import { okaoAxios } from '../../helpers/axios';
import { AlertColor } from '@mui/material/Alert';

export interface FoodArea {
  foodAreaId: string;
  city: string;
  name: string;
  description: string;
  centerLat: number;
  centerLng: number;
  radiusMeters: number;
  tagType: string;
  score: number;
  isManual: boolean;
  restaurantCount: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface FoodAreaDTO {
  city: string;
  name: string;
  description: string;
  centerLat: number;
  centerLng: number;
  radiusMeters: number;
  tagType: string;
  score: number;
}

export interface IFoodAreasSlice {
  status: 'idle' | 'loading' | 'error';
  foodAreas: FoodArea[];
  missingLatLngCount: number;
  shouldOpen: boolean;
  message: string;
  severity: AlertColor;
}

const initialState: IFoodAreasSlice = {
  status: 'idle',
  foodAreas: [],
  missingLatLngCount: 0,
  shouldOpen: false,
  message: '',
  severity: 'info'
};

interface ThunkConfig { state: { secure: { accessToken: string | undefined } } }

export const fetchAllFoodAreas = createAsyncThunk<FoodArea[], undefined, ThunkConfig>(
  'foodAreas/fetchAll',
  async (_, { getState }) => {
    const auth = getState().secure.accessToken ?? '';
    const response = await okaoAxios(auth).get<FoodArea[]>(
      `${process.env.REACT_APP_DIS_URL ?? window.location.origin}/admin/food-areas`
    );
    return response.data;
  }
);

export const createFoodArea = createAsyncThunk<FoodArea, FoodAreaDTO, ThunkConfig>(
  'foodAreas/create',
  async (area, { getState }) => {
    const auth = getState().secure.accessToken ?? '';
    const response = await okaoAxios(auth).post<FoodArea>(
      `${process.env.REACT_APP_DIS_URL ?? window.location.origin}/admin/food-areas`,
      area
    );
    return response.data;
  }
);

export const assignAllRestaurants = createAsyncThunk<{ status: string; message: string }, undefined, ThunkConfig>(
  'foodAreas/assignAll',
  async (_, { getState }) => {
    const auth = getState().secure.accessToken ?? '';
    const response = await okaoAxios(auth).post<{ status: string; message: string }>(
      `${process.env.REACT_APP_DIS_URL ?? window.location.origin}/admin/food-areas/assign-all`
    );
    return response.data;
  }
);

export const fetchMissingLatLngCount = createAsyncThunk<number, undefined, ThunkConfig>(
  'foodAreas/fetchMissingLatLngCount',
  async (_, { getState }) => {
    const auth = getState().secure.accessToken ?? '';
    const response = await okaoAxios(auth).get<{ count: number }>(
      `${process.env.REACT_APP_DIS_URL ?? window.location.origin}/admin/restaurants/missing-latlng/count`
    );
    return response.data.count;
  }
);

export const generateFoodAreas = createAsyncThunk<{ createdCount: number }, string, ThunkConfig>(
  'foodAreas/generate',
  async (city, { getState }) => {
    const auth = getState().secure.accessToken ?? '';
    const response = await okaoAxios(auth).post<{ createdCount: number }>(
      `${process.env.REACT_APP_DIS_URL ?? window.location.origin}/admin/food-areas/generate?city=${encodeURIComponent(city)}`
    );
    return response.data;
  }
);

export const generateFoodAreasFromMaps = createAsyncThunk<{ createdCount: number }, string, ThunkConfig>(
  'foodAreas/generateFromMaps',
  async (city, { getState }) => {
    const auth = getState().secure.accessToken ?? '';
    const response = await okaoAxios(auth).post<{ createdCount: number }>(
      `${process.env.REACT_APP_DIS_URL ?? window.location.origin}/admin/food-areas/generate-from-maps?city=${encodeURIComponent(city)}`
    );
    return response.data;
  }
);

export const generateAllFoodAreas = createAsyncThunk<{ status: string; message: string }, undefined, ThunkConfig>(
  'foodAreas/generateAll',
  async (_, { getState }) => {
    const auth = getState().secure.accessToken ?? '';
    const response = await okaoAxios(auth).post<{ status: string; message: string }>(
      `${process.env.REACT_APP_DIS_URL ?? window.location.origin}/admin/food-areas/generate-all`
    );
    return response.data;
  }
);

export const generateAllFoodAreasFromMaps = createAsyncThunk<{ status: string; message: string }, undefined, ThunkConfig>(
  'foodAreas/generateAllFromMaps',
  async (_, { getState }) => {
    const auth = getState().secure.accessToken ?? '';
    const response = await okaoAxios(auth).post<{ status: string; message: string }>(
      `${process.env.REACT_APP_DIS_URL ?? window.location.origin}/admin/food-areas/generate-all-from-maps`
    );
    return response.data;
  }
);

export const triggerNightlyLoad = createAsyncThunk<undefined, undefined, ThunkConfig>(
  'foodAreas/triggerNightlyLoad',
  async (_, { getState }): Promise<undefined> => {
    const auth = getState().secure.accessToken ?? '';
    await okaoAxios(auth).post(
      `${process.env.REACT_APP_DIS_URL ?? window.location.origin}/admin/restaurants/nightly-load`
    );
    return undefined;
  }
);

export const backfillLatLng = createAsyncThunk<undefined, undefined, ThunkConfig>(
  'foodAreas/backfillLatLng',
  async (_, { getState }): Promise<undefined> => {
    const auth = getState().secure.accessToken ?? '';
    await okaoAxios(auth).post(
      `${process.env.REACT_APP_DIS_URL ?? window.location.origin}/admin/restaurants/missing-latlng/backfill`
    );
    return undefined;
  }
);

export const foodAreasSlice = createSlice({
  name: 'foodAreas',
  initialState,
  reducers: {
    setOpen: (state, action: PayloadAction<boolean>) => {
      state.shouldOpen = action.payload;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(fetchAllFoodAreas.fulfilled, (state, action) => {
        state.foodAreas = action.payload;
        state.status = 'idle';
      })
      .addCase(createFoodArea.fulfilled, (state, action) => {
        state.foodAreas = [...state.foodAreas, action.payload];
        state.status = 'idle';
        state.shouldOpen = true;
        state.message = 'Food area created successfully';
        state.severity = 'success';
      })
      .addCase(assignAllRestaurants.fulfilled, (state) => {
        state.status = 'idle';
        state.shouldOpen = true;
        state.message = 'Assignment started — running in background';
        state.severity = 'success';
      })
      .addCase(fetchMissingLatLngCount.fulfilled, (state, action) => {
        state.missingLatLngCount = action.payload;
        state.status = 'idle';
      })
      .addCase(backfillLatLng.fulfilled, (state) => {
        state.status = 'idle';
        state.shouldOpen = true;
        state.message = 'Lat/lng backfill started — running in background';
        state.severity = 'success';
      })
      .addCase(triggerNightlyLoad.fulfilled, (state) => {
        state.status = 'idle';
        state.shouldOpen = true;
        state.message = 'Nightly load started — running in background for all cities';
        state.severity = 'success';
      })
      .addCase(generateFoodAreas.fulfilled, (state, action) => {
        state.status = 'idle';
        state.shouldOpen = true;
        state.message = `Generation complete — ${action.payload.createdCount} new area(s) created`;
        state.severity = 'success';
      })
      .addCase(generateFoodAreasFromMaps.fulfilled, (state, action) => {
        state.status = 'idle';
        state.shouldOpen = true;
        state.message = `Maps generation complete — ${action.payload.createdCount} new area(s) created`;
        state.severity = 'success';
      })
      .addCase(generateAllFoodAreas.fulfilled, (state) => {
        state.status = 'idle';
        state.shouldOpen = true;
        state.message = 'Bulk DB generation started for all cities — check logs for progress';
        state.severity = 'success';
      })
      .addCase(generateAllFoodAreasFromMaps.fulfilled, (state) => {
        state.status = 'idle';
        state.shouldOpen = true;
        state.message = 'Bulk Maps generation started for all cities — check logs for progress';
        state.severity = 'success';
      })
      .addMatcher(
        isAnyOf(
          fetchAllFoodAreas.pending,
          createFoodArea.pending,
          assignAllRestaurants.pending,
          fetchMissingLatLngCount.pending,
          backfillLatLng.pending,
          triggerNightlyLoad.pending,
          generateFoodAreas.pending,
          generateFoodAreasFromMaps.pending,
          generateAllFoodAreas.pending,
          generateAllFoodAreasFromMaps.pending
        ),
        (state) => {
          state.status = 'loading';
        }
      )
      .addMatcher(
        isAnyOf(
          fetchAllFoodAreas.rejected,
          createFoodArea.rejected,
          assignAllRestaurants.rejected,
          fetchMissingLatLngCount.rejected,
          backfillLatLng.rejected,
          triggerNightlyLoad.rejected,
          generateFoodAreas.rejected,
          generateFoodAreasFromMaps.rejected,
          generateAllFoodAreas.rejected,
          generateAllFoodAreasFromMaps.rejected
        ),
        (state, action) => {
          state.status = 'error';
          state.shouldOpen = true;
          state.severity = 'error';
          state.message = 'An error occurred. Please try again.';
          console.error(action.error);
        }
      );
  }
});

interface SliceState { foodAreas: IFoodAreasSlice }

export const selectFoodAreas = (state: SliceState): FoodArea[] => state.foodAreas.foodAreas;
export const selectMissingLatLngCount = (state: SliceState): number => state.foodAreas.missingLatLngCount;
export const selectFoodAreasShouldOpen = (state: SliceState): boolean => state.foodAreas.shouldOpen;
export const selectFoodAreasMessage = (state: SliceState): string => state.foodAreas.message;
export const selectFoodAreasSeverity = (state: SliceState): AlertColor => state.foodAreas.severity;
export const selectFoodAreasIsLoading = (state: SliceState): boolean => state.foodAreas.status === 'loading';

export const { setOpen } = foodAreasSlice.actions;
export default foodAreasSlice.reducer;
